import React, { useMemo, useState, useEffect } from 'react';
import { useAllOrders } from '../../context/AllOrdersContext';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { ChartBarIcon, ClipboardListIcon, StarIcon } from '../../components/icons';
import { parseDate, formatDateReadable, getStartOfWeek } from '../../utils/dateUtils';
import { db } from '../../firebase/client';
import { 
    collection, 
    query, 
    where, 
    getAggregateFromServer, 
    sum, 
    or, 
    orderBy, 
    limit, 
    getDocs,
    Timestamp 
} from 'firebase/firestore';
import { PlacedOrder } from '../../types';

interface DailyStat {
    date: string;
    displayDate: string;
    total: number;
    count: number;
}

interface WeeklyStat {
    key: string;
    label: string;
    total: number;
    count: number;
}

const AdminAccountingScreen: React.FC = () => {
    const { loading: contextLoading } = useAllOrders();
    const { getRestaurantById, restaurants, mapOrderFromDB } = useData();
    const { currentUser } = useAuth();

    const [stats, setStats] = useState({
        totalRevenue: 0,
        todayRevenue: 0,
        weeklyRevenue: 0,
        monthlyRevenue: 0,
        fees: 0,
        debt: 0,
        isCommission: false
    });

    const [accountingOrders, setAccountingOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const restaurant = useMemo(() => {
        return getRestaurantById(currentUser?.restaurantId || '') || restaurants.find(r => r.ownerId === currentUser?.uid);
    }, [currentUser, restaurants, getRestaurantById]);

    const restaurantId = restaurant?.id || currentUser?.restaurantId;

    // Helper interne pour mapper les commandes sans dépendre du contexte
    const internalMapOrder = (id: string, o: any): PlacedOrder => ({
        id,
        customerName: o.customerName || o.customer_name,
        customerPhone: o.customerPhone || o.customer_phone || 'Non renseigné',
        customerAddress: o.customerAddress || o.customer_address,
        customerId: o.customerId || o.customer_id,
        restaurantId: o.restaurantId || o.restaurant_id || o.id_restaurant,
        restaurantName: o.restaurantName || o.restaurant_name,
        items: o.items || [],
        totalPrice: Number(o.totalPrice || o.total_price || 0),
        deliveryFee: Number(o.deliveryFee || o.delivery_fee || 0),
        status: o.status,
        deliveryType: o.deliveryType || o.delivery_type || 'delivery',
        timestamp: parseDate(o.createdAt || o.timestamp || o.preparationAt || o.assignedAt, new Date(), o.orderId),
        livreurId: o.livreurId || o.livreur_id,
        orderId: o.orderId
    });

    useEffect(() => {
        if (!restaurantId) return;

        async function fetchStats() {
            try {
                setLoading(true);
                const now = new Date();
                const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const startOfWeek = getStartOfWeek();

                // TEST: Fetch the first 5 orders from the entire DB to see what IDs are there
                const rawTest = await getDocs(query(collection(db, 'orders'), limit(5)));
                const rawIds = rawTest.docs.map(d => d.data().restaurantId || d.data().id_restaurant || 'no-id').join(', ');
                const rId = restaurantId.trim();

                // 1. Server-Side Aggregation (Absolute accuracy, infinite history)
                const fetchSum = async (field: string) => {
                    try {
                        const q = query(
                            collection(db, 'orders'),
                            where(field, '==', rId)
                        );
                        return await getAggregateFromServer(q, {
                            totalPrice: sum('totalPrice'),
                            deliveryFee: sum('deliveryFee')
                        });
                    } catch (e) {
                        return null;
                    }
                };

                // Fetch total raw sums from server
                const [agg1, agg2] = await Promise.all([
                    fetchSum('restaurantId'),
                    fetchSum('id_restaurant')
                ]);

                let totalRev = 0;
                let totalDel = 0;
                [agg1, agg2].forEach(a => {
                    if (a) {
                        totalRev += a.data().totalPrice || 0;
                        totalDel += a.data().deliveryFee || 0;
                    }
                });

                // 2. Local History for breakdown (Last 500)
                const fetchHistory = async (field: string, value: string) => {
                    const q = query(
                        collection(db, 'orders'),
                        where(field, '==', value),
                        limit(500)
                    );
                    return getDocs(q);
                };

                const [hSnap1, hSnap2] = await Promise.all([
                    fetchHistory('restaurantId', rId),
                    fetchHistory('id_restaurant', rId)
                ]);
                
                const allDocs = [...hSnap1.docs, ...hSnap2.docs];
                const orders = Array.from(new Map(allDocs.map(doc => [doc.id, doc])).values())
                    .map(doc => internalMapOrder(doc.id, doc.data()))
                    .filter(o => ['Livrée', 'Terminée', 'Completed', 'Livré'].includes(o.status));

                // Sort manually
                orders.sort((a, b) => parseDate(b.timestamp).getTime() - parseDate(a.timestamp).getTime());

                setAccountingOrders(orders);

                // 3. Final Calculations
                const localTotal = orders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
                const totalNetRevenue = (totalRev - totalDel) || localTotal;
                
                const todayNetRevenue = orders
                    .filter(o => parseDate(o.timestamp).getTime() >= startOfToday.getTime())
                    .reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);

                const weeklyNetRevenue = orders
                    .filter(o => parseDate(o.timestamp).getTime() >= startOfWeek.getTime())
                    .reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);

                const monthlyNetRevenue = orders
                    .filter(o => {
                        const date = parseDate(o.timestamp);
                        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                    })
                    .reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);

                // Commission logic
                const isCommission = restaurant?.subscriptionType === 'commission';
                const commissionRate = isCommission ? 0.05 : 0;
                const commStart = parseDate(restaurant?.commissionStartedAt, new Date(0));
                
                const commissionableRevenue = orders
                    .filter(o => parseDate(o.timestamp) >= commStart)
                    .reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
                
                const fees = Math.round(commissionableRevenue * commissionRate);
                const debt = Number(restaurant?.commissionDebt || 0);

                setStats({
                    totalRevenue: totalNetRevenue,
                    todayRevenue: todayNetRevenue,
                    weeklyRevenue: weeklyNetRevenue,
                    monthlyRevenue: monthlyNetRevenue,
                    fees: fees + debt,
                    currentFees: fees,
                    debt: debt,
                    isCommission
                });

            } catch (err) {
                console.error("Error fetching accounting stats:", err);
            } finally {
                setLoading(false);
            }
        }

        fetchStats();
    }, [restaurantId, restaurant?.subscriptionType, restaurant?.commissionStartedAt, restaurant?.commissionDebt]);

    // Grouping logic remains the same but uses accountingOrders instead of context
    const validOrders = accountingOrders;

    // 3. Groupement par Jour
    const dailyStats = useMemo(() => {
        const statsMap: Record<string, DailyStat> = {};
        
        validOrders.forEach(order => {
            const date = parseDate(order.timestamp);
            const dateKey = date.toISOString().split('T')[0];
            
            if (!statsMap[dateKey]) {
                statsMap[dateKey] = {
                    date: dateKey,
                    displayDate: formatDateReadable(date),
                    total: 0,
                    count: 0
                };
            }
            statsMap[dateKey].total += (order.totalPrice - (order.deliveryFee || 0));
            statsMap[dateKey].count += 1;
        });

        return Object.values(statsMap).sort((a, b) => b.date.localeCompare(a.date));
    }, [validOrders]);

    // 4. Groupement par Semaine (Helper pour numéro de semaine ISO)
    const getWeekNumber = (d: Date) => {
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    const weeklyStats = useMemo(() => {
        const statsMap: Record<string, WeeklyStat> = {};

        validOrders.forEach(order => {
            const date = parseDate(order.timestamp);
            const weekNum = getWeekNumber(date);
            const year = date.getFullYear();
            const key = `${year}-W${weekNum}`;

            if (!statsMap[key]) {
                statsMap[key] = {
                    key,
                    label: `Semaine ${weekNum} - ${year}`,
                    total: 0,
                    count: 0
                };
            }
            statsMap[key].total += (order.totalPrice - (order.deliveryFee || 0));
            statsMap[key].count += 1;
        });

        return Object.values(statsMap).sort((a, b) => b.key.localeCompare(a.key));
    }, [validOrders]);


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="w-8 h-8 border-4 border-[#E31E24] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-4 bg-gray-50 min-h-screen pb-24">
            <header className="py-4 mb-6">
                <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Comptabilité</h1>
                <p className="text-gray-600 font-medium">Suivi de vos revenus et performances.</p>
            </header>

            {/* --- Cartes KPIs Principales --- */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Aujourd'hui</p>
                    <p className="text-xl font-black text-gray-900">{stats.todayRevenue.toLocaleString()} <span className="text-[10px] font-bold text-gray-400">DA</span></p>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Semaine</p>
                    <p className="text-xl font-black text-gray-900">{stats.weeklyRevenue.toLocaleString()} <span className="text-[10px] font-bold text-gray-400">DA</span></p>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Mois</p>
                    <p className="text-xl font-black text-gray-900">{stats.monthlyRevenue.toLocaleString()} <span className="text-[10px] font-bold text-gray-400">DA</span></p>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden ring-2 ring-[#E31E24]/5">
                    <p className="text-[10px] font-black text-[#E31E24] uppercase tracking-widest mb-1">C.A. Total</p>
                    <p className="text-xl font-black text-gray-900">{stats.totalRevenue.toLocaleString()} <span className="text-[10px] font-bold text-gray-400">DA</span></p>
                    <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">Depuis création</p>
                </div>
            </div>

            {/* Section Commission (Visible uniquement si abonnement Commission) */}
            {stats.isCommission && (
                <div className="mb-8">
                    <div className="bg-[#E31E24] p-6 rounded-3xl shadow-xl flex flex-col md:flex-row md:items-center justify-center text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <StarIcon className="w-20 h-20 text-white" />
                        </div>
                        <div className="relative z-10 text-center">
                            <p className="text-[10px] font-black text-white/70 uppercase tracking-widest mb-1">Frais Yumii</p>
                            <div className="flex items-baseline gap-2 justify-center">
                                <p className="text-4xl font-black">{stats.fees.toLocaleString()}</p>
                                <span className="text-sm font-bold text-white/70">DA</span>
                            </div>
                            {stats.debt > 0 && (
                                <p className="text-[9px] text-white/60 font-medium mt-1">
                                    Inclut {stats.debt.toLocaleString()} DA de dettes
                                </p>
                            )}
                            <p className="text-[10px] text-white/50 italic mt-1 font-medium">Commission de service Yumii sur vos ventes</p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Listes Détaillées --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Vue Journalière */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center">
                        <h2 className="text-lg font-bold text-gray-900">Détail par Jour</h2>
                    </div>
                    <div className="p-4 max-h-[400px] overflow-y-auto">
                        {dailyStats.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 font-medium italic">
                                Aucune commande terminée.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {dailyStats.map((stat) => (
                                    <div key={stat.date} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors">
                                        <div>
                                            <p className="text-sm font-bold text-gray-800 capitalize">{stat.displayDate}</p>
                                        </div>
                                        <p className="text-md font-black text-gray-900">{stat.total.toLocaleString()} DA</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Vue Hebdomadaire */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center">
                        <h2 className="text-lg font-bold text-gray-900">Détail par Semaine</h2>
                    </div>
                    <div className="p-4 max-h-[400px] overflow-y-auto">
                        {weeklyStats.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 font-medium italic">
                                Aucune donnée.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {weeklyStats.map((stat) => (
                                    <div key={stat.key} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50/50 border border-gray-50">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center font-black text-xs">
                                                W{stat.key.split('-W')[1]}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{stat.label}</p>
                                            </div>
                                        </div>
                                        <span className="text-md font-black text-[#E31E24]">{stat.total.toLocaleString()} DA</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAccountingScreen;

